import { Box, Text } from '@chakra-ui/react';
import React from 'react';
import type { BondPaymentCashFlowRecord, CashFlow } from '~/types';
import { compareDates, formatDateForDisplay } from '~/utils/date-utils';

interface CashFlowsTableProps {
  cashFlows: CashFlow[];
  title?: string;
  isFullscreen?: boolean;
}

// Utility function to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Convert CashFlow array to table-friendly format
const convertToTableData = (cashFlows: CashFlow[]): CashFlow[] => {
  return cashFlows.sort((a, b) => compareDates(a.paymentDate, b.paymentDate));
};

// Get maximum number of bond series across all cash flows
const getMaxBondSeries = (cashFlows: CashFlow[]): number => {
  return Math.max(...cashFlows.map((flow) => flow.bondPayments.length), 0);
};

// Sort bond payments by issue date
const sortBondPaymentsByIssueDate = (
  bondPayments: BondPaymentCashFlowRecord[]
): BondPaymentCashFlowRecord[] => {
  return [...bondPayments].sort((a, b) => compareDates(a.bondCFIssueDate, b.bondCFIssueDate));
};

interface ColumnDefinition {
  key: string;
  header: string;
  accessor: (item: CashFlow) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  category?: string;
}

export const CashFlowsTable: React.FC<CashFlowsTableProps> = ({
  cashFlows,
  isFullscreen = false,
}) => {
  // Convert to table data and calculate max bonds
  const tableData = convertToTableData(cashFlows);
  const maxBondSeries = getMaxBondSeries(tableData);

  // Column visibility - all categories visible by default
  const visibleCategories = new Set(['date', 'revenue', 'debt', 'bonds', 'drf']);

  // Generate dynamic columns based on actual data
  const generateColumns = (): ColumnDefinition[] => {
    const staticColumns: ColumnDefinition[] = [
      // Date & Context
      {
        key: 'paymentDate',
        header: 'Date',
        accessor: (item) => formatDateForDisplay(item.paymentDate),
        width: '100px',
        align: 'center',
        category: 'date',
      },
      {
        key: 'relatedTaxBillYear',
        header: 'Tax Yr',
        accessor: (item) => item.relatedTaxBillYear,
        width: '70px',
        align: 'center',
        category: 'date',
      },
      {
        key: 'relatedFiscalYearEndingDate',
        header: 'FYE',
        accessor: (item) => item.relatedFiscalYearEndingDate.slice(0, 7), // YYYY-MM
        width: '80px',
        align: 'center',
        category: 'date',
      },
      {
        key: 'flags',
        header: 'Flags',
        accessor: (item) => {
          const flags = [];
          if (item.isActive) flags.push('A');
          if (item.isInterestOnly) flags.push('I');
          if (item.isFinalFiscalYearPayment) flags.push('F');
          return flags.join(' ');
        },
        width: '60px',
        align: 'center',
        category: 'date',
      },

      // Revenue & Millage
      {
        key: 'millageRate',
        header: 'Millage',
        accessor: (item) => item.millageRate.toFixed(2),
        width: '80px',
        align: 'right',
        category: 'revenue',
      },
      {
        key: 'totalRevenueForTaxYear',
        header: 'Revenue',
        accessor: (item) => formatCurrency(item.totalRevenueForTaxYear),
        width: '120px',
        align: 'right',
        category: 'revenue',
      },
      {
        key: 'paymentRevenuePercentage',
        header: 'Rev %',
        accessor: (item) => `${(item.paymentRevenuePercentage * 100).toFixed(1)}%`,
        width: '70px',
        align: 'right',
        category: 'revenue',
      },
      {
        key: 'paymentRevenuePortion',
        header: 'Payment Rev',
        accessor: (item) => formatCurrency(item.paymentRevenuePortion),
        width: '110px',
        align: 'right',
        category: 'revenue',
      },

      // Debt Service
      {
        key: 'debtPaymentAmount',
        header: 'Existing Debt',
        accessor: (item) => formatCurrency(item.debtPaymentAmount),
        width: '110px',
        align: 'right',
        category: 'debt',
      },
      {
        key: 'revenueMinusExistingDebt',
        header: 'Net Revenue',
        accessor: (item) => formatCurrency(item.revenueMinusExistingDebt),
        width: '110px',
        align: 'right',
        category: 'debt',
      },
      {
        key: 'interestObligation',
        header: 'Int Oblig',
        accessor: (item) => formatCurrency(item.interestObligation || 0),
        width: '100px',
        align: 'right',
        category: 'debt',
      },
      {
        key: 'availableForBonds',
        header: 'Available',
        accessor: (item) => formatCurrency(item.availableForBonds),
        width: '110px',
        align: 'right',
        category: 'debt',
      },
    ];

    // Dynamic bond series columns
    const bondColumns: ColumnDefinition[] = [];
    for (let i = 1; i <= maxBondSeries; i++) {
      const bondIndex = i - 1;
      bondColumns.push(
        {
          key: `bond${i}Active`,
          header: `B${i}-A`,
          accessor: (item) => {
            const sortedBonds = sortBondPaymentsByIssueDate(item.bondPayments);
            return sortedBonds[bondIndex]?.isActive ? 'A' : '';
          },
          width: '40px',
          align: 'center',
          category: 'bonds',
        },
        {
          key: `bond${i}Principal`,
          header: `B${i}-Principal`,
          accessor: (item) => {
            const sortedBonds = sortBondPaymentsByIssueDate(item.bondPayments);
            return sortedBonds[bondIndex] ? formatCurrency(sortedBonds[bondIndex].principal) : '';
          },
          width: '100px',
          align: 'right',
          category: 'bonds',
        },
        {
          key: `bond${i}Rate`,
          header: `B${i}-IR`,
          accessor: (item) => {
            const sortedBonds = sortBondPaymentsByIssueDate(item.bondPayments);
            return sortedBonds[bondIndex]
              ? `${(sortedBonds[bondIndex].interestRate * 100).toFixed(2)}%`
              : '';
          },
          width: '60px',
          align: 'right',
          category: 'bonds',
        },
        {
          key: `bond${i}NewInterest`,
          header: `B${i}-NewI`,
          accessor: (item) => {
            const sortedBonds = sortBondPaymentsByIssueDate(item.bondPayments);
            return sortedBonds[bondIndex] ? formatCurrency(sortedBonds[bondIndex].newInterest) : '';
          },
          width: '80px',
          align: 'right',
          category: 'bonds',
        },
        {
          key: `bond${i}FutureInterest`,
          header: `B${i}-IOb`,
          accessor: (item) => {
            const sortedBonds = sortBondPaymentsByIssueDate(item.bondPayments);
            return sortedBonds[bondIndex]
              ? formatCurrency(sortedBonds[bondIndex].futureInterestObligation)
              : '';
          },
          width: '80px',
          align: 'right',
          category: 'bonds',
        },
        {
          key: `bond${i}TotalInterest`,
          header: `B${i}-TInt`,
          accessor: (item) => {
            const sortedBonds = sortBondPaymentsByIssueDate(item.bondPayments);
            return sortedBonds[bondIndex]
              ? formatCurrency(sortedBonds[bondIndex].totalInterest)
              : '';
          },
          width: '80px',
          align: 'right',
          category: 'bonds',
        },
        {
          key: `bond${i}TotalPayment`,
          header: `B${i}-TPay`,
          accessor: (item) => {
            const sortedBonds = sortBondPaymentsByIssueDate(item.bondPayments);
            return sortedBonds[bondIndex]
              ? formatCurrency(sortedBonds[bondIndex].totalPayment)
              : '';
          },
          width: '100px',
          align: 'right',
          category: 'bonds',
        }
      );
    }

    // Final columns
    const finalColumns: ColumnDefinition[] = [
      {
        key: 'totalBondPayment',
        header: 'CF Payment',
        accessor: (item) => formatCurrency(item.totalBondPayment),
        width: '110px',
        align: 'right',
        category: 'bonds',
      },
      {
        key: 'drfStartingBalance',
        header: 'DRF Start',
        accessor: (item) => formatCurrency(item.drfStartingBalance),
        width: '110px',
        align: 'right',
        category: 'drf',
      },
      {
        key: 'drfAdjustment',
        header: 'DRF Adjust',
        accessor: (item) => formatCurrency(item.drfAdjustment),
        width: '110px',
        align: 'right',
        category: 'drf',
      },
      {
        key: 'drfEndingBalance',
        header: 'DRF End',
        accessor: (item) => formatCurrency(item.drfEndingBalance),
        width: '110px',
        align: 'right',
        category: 'drf',
      },
    ];

    return [...staticColumns, ...bondColumns, ...finalColumns];
  };

  const columns = generateColumns();

  // Filter columns based on visibility - memoize to prevent dependency issues
  const visibleColumns = React.useMemo(
    () => columns.filter((col) => !col.category || visibleCategories.has(col.category)),
    [columns, visibleCategories]
  );

  // Calculate footer totals for visible columns - must be before early returns
  const footerTotals = React.useMemo(() => {
    if (!cashFlows || cashFlows.length === 0) return null;
    if (tableData.length === 0) return null;

    const totals: Record<string, number> = {};

    // Calculate totals for numeric columns
    visibleColumns.forEach((col) => {
      if (
        col.category === 'bonds' ||
        col.key === 'totalBondPayment' ||
        col.key === 'drfStartingBalance' ||
        col.key === 'drfAdjustment' ||
        col.key === 'drfEndingBalance'
      ) {
        totals[col.key] = tableData.reduce((sum, item) => {
          const value = col.accessor(item);
          // Extract numeric value from formatted currency strings
          if (typeof value === 'string' && value.startsWith('$')) {
            return sum + (parseFloat(value.replace(/[$,]/g, '')) || 0);
          }
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
      }
    });

    return totals;
  }, [tableData, visibleColumns, cashFlows]);

  // Handle empty data - must be after hooks
  if (!cashFlows || cashFlows.length === 0) {
    return (
      <Box>
        <Text color="gray.500">No cash flow data available</Text>
      </Box>
    );
  }

  return (
    <Box width="100%" maxWidth="100%" overflowX="hidden">
      {/* Advanced table with sticky header and full width */}
      <Box
        position="relative"
        width="100%"
        maxHeight={isFullscreen ? 'calc(100vh - 300px)' : '600px'}
        overflowY="auto"
        overflowX="auto"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
      >
        <Box as="table" width="100%" borderCollapse="collapse" fontSize="sm">
          {/* Sticky Header */}
          <Box as="thead">
            <Box as="tr" position="sticky" top={0} zIndex={10} bg="gray.100">
              {visibleColumns.map((col) => (
                <Box
                  as="th"
                  key={col.key}
                  textAlign={col.align || 'left'}
                  p={2}
                  borderBottom="2px solid"
                  borderBottomColor="gray.300"
                  bg="gray.100"
                  fontWeight="semibold"
                  fontSize="xs"
                  width={col.width}
                  minWidth={col.width}
                  maxWidth={col.width}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                >
                  {col.header}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Table Body */}
          <Box as="tbody">
            {tableData.map((item) => (
              <Box
                as="tr"
                key={item.paymentDate}
                _hover={{ bg: 'blue.50' }}
                borderBottom="1px solid"
                borderBottomColor="gray.100"
              >
                {visibleColumns.map((col) => (
                  <Box
                    as="td"
                    key={`${col.key}-${item.paymentDate}`}
                    textAlign={col.align || 'left'}
                    p={2}
                    fontSize="xs"
                    width={col.width}
                    minWidth={col.width}
                    maxWidth={col.width}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {col.accessor(item)}
                  </Box>
                ))}
              </Box>
            ))}

            {/* Footer Row */}
            {footerTotals && (
              <Box
                as="tr"
                fontWeight="bold"
                bg="gray.50"
                borderTop="2px solid"
                borderTopColor="gray.200"
              >
                {visibleColumns.map((col) => {
                  const totalValue = footerTotals[col.key];
                  const isNumericColumn =
                    col.category === 'bonds' ||
                    col.key === 'totalBondPayment' ||
                    col.key === 'drfStartingBalance' ||
                    col.key === 'drfAdjustment' ||
                    col.key === 'drfEndingBalance';

                  return (
                    <Box
                      as="td"
                      key={`footer-${col.key}`}
                      textAlign={col.align || 'left'}
                      p={2}
                      fontSize="xs"
                      width={col.width}
                      minWidth={col.width}
                      maxWidth={col.width}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {isNumericColumn && totalValue !== undefined
                        ? formatCurrency(totalValue)
                        : col.key === 'paymentDate'
                          ? 'TOTALS'
                          : ''}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Footer information */}
      <Text mt={2} fontSize="sm" color="gray.600">
        Showing all {tableData.length} payment records across {visibleColumns.length} of{' '}
        {columns.length} columns
      </Text>
    </Box>
  );
};
